'use client';

import React, { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import type { Card as CardType, StepConfig, FormInstance, FormValues } from '../../types';
import { SectionCardContent } from './SectionCardContent';
import { useCardInstances } from '../../hooks/useCardInstances';
import { getAddMoreConfig } from '../../utils/addMoreConfig';
import { mergeFormValues } from '../../utils/valueMerger';

interface SectionCardProps {
  card: CardType;
  steps: StepConfig[];
  completedForms: Record<string, FormInstance[]>;
  values?: FormValues;
  stepsById?: Record<string, StepConfig>;
  localStorageKey?: string;
  onEdit: (instanceId: string) => void;
  onAdd?: () => void;
  onStart?: () => void;
  isCTAEnabled?: boolean;
  startCTAText?: string; // Custom text for "Start" button
  isFirstIncomplete?: boolean; // Whether this is the first incomplete card in the section
  alignment?: 'horizontal' | 'vertical';
  showCharityEmptyState?: boolean;
  onNavigateToCharityStep?: () => void;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  card,
  steps,
  completedForms,
  values = {},
  stepsById = {},
  localStorageKey,
  onEdit,
  onAdd,
  isCTAEnabled = true,
  showCharityEmptyState,
  onNavigateToCharityStep,
}) => {
  const { instances, hasData, showCTA } = useCardInstances(card, completedForms);

  // Check if this is card-distribution-estate and if children should be shown
  const shouldShowChildren = useMemo(() => {
    if (card.id !== 'card-distribution-estate') return false;

    // Merge values to check condition
    const mergedValues = mergeFormValues(values, completedForms, localStorageKey);
    const estateDistributionPreference = mergedValues.estateDistributionPreference;

    // Check if the specific radio option is selected (spouse-first or children-only equal share)
    return (
      estateDistributionPreference ===
        'My spouse/partner/civil partner. If they die before me, I want an equal share of my estate to pass to my children. If any of my children pass away before me, then their share will pass down to their children and so on down the family tree' ||
      estateDistributionPreference ===
        'I want an equal share of my estate to pass to my children. If any of my children pass away before me, then their share will pass down to their children and so on down the family tree'
    );
  }, [card.id, values, completedForms, localStorageKey]);

  const shouldShowSpousePartnerDetails = useMemo(() => {
    const isEstateDistributionCard = card.id === 'card-distribution-estate';
    const isExecutorCard = card.id === 'card-executor' || card.id === 'card-executor-choice';
    if (!isEstateDistributionCard && !isExecutorCard) return false;

    // Merge values to check condition
    const mergedValues = mergeFormValues(values, completedForms, localStorageKey);

    if (isEstateDistributionCard) {
      const estateDistributionPreference = mergedValues.estateDistributionPreference;
      return (
        estateDistributionPreference ===
          'My spouse/partner/civil partner. If they die before me, I want an equal share of my estate to pass to my children. If any of my children pass away before me, then their share will pass down to their children and so on down the family tree' ||
        estateDistributionPreference ===
          'My spouse/partner/civil partner. If they die before me, I want my estate to pass to the people or charities in the chosen amounts or percentages'
      );
    }

    const executorChoice = mergedValues.executorChoice;
    return (
      executorChoice ===
        'I want Irwin Mitchell and my spouse/partner/civil partner to be my Executor jointly' ||
      executorChoice ===
        "I want my spouse/partner/civil partner to be my Executor. I'd like Irwin Mitchell to act as substitute if my chosen Executor can't carry out their duties for any reason"
    );
  }, [card.id, values, completedForms, localStorageKey]);

  const showSpousePartnerDob = useMemo(() => {
    return card.id === 'card-distribution-estate';
  }, [card.id]);

  const spousePartnerRelationshipLabel = useMemo(() => {
    const mergedValues = mergeFormValues(values, completedForms, localStorageKey);
    return mergedValues.maritalStatus as string | undefined;
  }, [values, completedForms, localStorageKey]);

  // Get child instances and steps if needed
  const childInstances = useMemo(() => {
    if (!shouldShowChildren) return [];
    // Combine instances from both children-group and spousePartnerChildrenFromPreviousRelationship-group
    const childrenInstances = completedForms['children-group'] || [];
    const spousePartnerChildrenInstances =
      completedForms['spousePartnerChildrenFromPreviousRelationship-group'] || [];
    return [...childrenInstances, ...spousePartnerChildrenInstances];
  }, [shouldShowChildren, completedForms]);

  const childSteps = useMemo(() => {
    if (!shouldShowChildren) return [];
    // Get steps from both step-4-5-relationships and step-5-4-spouse-partner-children-previous-relationship
    const childrenStep = stepsById['step-4-5-relationships'];
    const spousePartnerChildrenStep =
      stepsById['step-5-4-spouse-partner-children-previous-relationship'];
    const steps = [];
    if (childrenStep) steps.push(childrenStep);
    if (spousePartnerChildrenStep) steps.push(spousePartnerChildrenStep);
    return steps;
  }, [shouldShowChildren, stepsById]);

  const spousePartnerInstance = useMemo(() => {
    if (!shouldShowSpousePartnerDetails) return [];
    const spousePartnerPersonalDetailsInstance =
      completedForms['spouse-partner-personal-details-group'] || [];
    return [...spousePartnerPersonalDetailsInstance];
  }, [completedForms, shouldShowSpousePartnerDetails]);

  // Check if this is an addMore card by checking if any step has addMoreConfig with matching groupKey
  const isAddMoreCard = React.useMemo(() => {
    return steps.some((step) => {
      const addMoreConfig = getAddMoreConfig(step);
      return addMoreConfig && addMoreConfig.groupKey === card.groupKey;
    });
  }, [steps, card.groupKey]);

  // Don't render card if no data and showCTA is false (unless we have children to show)
  if (!hasData && !showCTA && childInstances.length === 0 && !showCharityEmptyState) {
    return null;
  }

  // For addMore cards, don't show "Add" button on section list - it should only show on the addMore step
  // For regular cards, show "Add" button if conditions are met
  const shouldShowAdd =
    !isAddMoreCard &&
    hasData &&
    card.maxInstances !== undefined &&
    instances.length < card.maxInstances &&
    showCTA;

  // Only show Start button if this is the first incomplete card in the section
  const { container } = TAILWIND_VARIANTS();
  const alignment = card.alignment || 'horizontal';

  return (
    <>
      {hasData && (
        <div className={container()}>
          <SectionCardContent
            instances={instances}
            steps={steps}
            cardTitle={card.title}
            cardId={card.id}
            card={card}
            onEdit={onEdit}
            showAddButton={shouldShowAdd}
            onAdd={onAdd}
            isCTAEnabled={isCTAEnabled}
            alignment={alignment}
            childInstances={shouldShowChildren ? childInstances : undefined}
            childSteps={shouldShowChildren ? childSteps : undefined}
            childCardTitle="Children"
            spousePartnerInstance={
              shouldShowSpousePartnerDetails ? spousePartnerInstance : undefined
            }
            showSpousePartnerDob={showSpousePartnerDob}
            spousePartnerRelationshipLabel={spousePartnerRelationshipLabel}
          />
        </div>
      )}
      {/* Show children even if main card has no data */}
      {!hasData && shouldShowChildren && childInstances.length > 0 && (
        <div className={container()}>
          <SectionCardContent
            instances={childInstances}
            steps={childSteps}
            cardTitle="Children"
            cardId="card-children"
            card={undefined}
            onEdit={(instanceId) => {
              // Navigate to children card edit - use card-children groupKey
              onEdit(instanceId);
            }}
            showAddButton={false}
            isCTAEnabled={isCTAEnabled}
            alignment={alignment}
          />
        </div>
      )}
      {/* Show "No Data Added for Charity" when all 3 groups empty and section in progress/completed */}
      {!hasData && showCharityEmptyState && onNavigateToCharityStep && (
        <div className={container()}>
          <SectionCardContent
            instances={[]}
            steps={steps}
            cardTitle={card.title}
            cardId={card.id}
            card={card}
            onEdit={() => {}}
            showAddButton={false}
            isCTAEnabled={isCTAEnabled}
            alignment={alignment}
            emptyStateLabel="No Data Added for Charity"
            onEmptyStateEdit={onNavigateToCharityStep}
          />
        </div>
      )}
    </>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['w-full'],
  },
});
